import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Tooltip from '@mui/material/Tooltip';
import { httpsCallable } from 'firebase/functions';
import { functions as firebaseFunctions } from '../firebase';

const createMetadataFilter = () => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  key: '',
  value: '',
});

const cloneMetadataFilters = (filters = []) => {
  const entries = [];

  filters.forEach((filter) => {
    const key = typeof filter?.key === 'string' ? filter.key : '';
    const values = Array.isArray(filter?.values) ? filter.values.map((value) => String(value)) : [];

    if (values.length > 0) {
      values.forEach((value) => {
        entries.push({
          id: createMetadataFilter().id,
          key,
          value,
        });
      });
      return;
    }

    entries.push({
      id: filter?.id || createMetadataFilter().id,
      key,
      value: typeof filter?.value === 'string' ? filter.value : '',
    });
  });

  return entries;
};

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const addDays = (date, days) => new Date(date.getTime() + days * 86400000);
const startOfWeekMon = (date) => {
  const weekday = date.getDay() || 7;
  return startOfDay(addDays(date, 1 - weekday));
};
const endOfWeekSun = (date) => endOfDay(addDays(startOfWeekMon(date), 6));
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));

const getRangeFromPreset = (preset) => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  if (preset === 'today') return { from: todayStart, to: todayEnd };
  if (preset === 'yesterday') {
    const yesterday = addDays(todayStart, -1);
    return { from: yesterday, to: endOfDay(yesterday) };
  }
  if (preset === 'last7') return { from: addDays(todayStart, -6), to: todayEnd };
  if (preset === 'thisWeek') return { from: startOfWeekMon(now), to: todayEnd };
  if (preset === 'lastWeek') {
    const lastWeekEnd = endOfWeekSun(addDays(now, -7));
    return { from: addDays(lastWeekEnd, -6), to: lastWeekEnd };
  }
  if (preset === 'thisMonth') return { from: startOfMonth(now), to: todayEnd };
  if (preset === 'lastMonth') {
    const lastMonthDate = addDays(startOfMonth(now), -1);
    return { from: startOfMonth(lastMonthDate), to: endOfMonth(lastMonthDate) };
  }

  return { from: null, to: null };
};

const normalizeCallableErrorMessage = (error) => {
  const errorCode = trimString(error?.code);

  if (errorCode === 'functions/failed-precondition') {
    return 'Ընտրված ֆիլտրերով ավարտված վերլուծություններ չկան։';
  }

  if (errorCode === 'functions/not-found') {
    return 'Ստուգացուցակը չի գտնվել։';
  }

  if (errorCode === 'functions/permission-denied') {
    return 'Արտահանումը հասանելի չէ այս ստուգացուցակի համար։';
  }

  return 'Չհաջողվեց ստեղծել CSV արտահանումը։';
};

const ChecklistAnalysisExportDialog = ({
  open,
  onClose,
  checklist,
  metadataKeyOptions = [],
  metadataValuesByKey = {},
  defaultMetadataFilters = [],
}) => {
  const [datePreset, setDatePreset] = useState('last7');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [metadataFilters, setMetadataFilters] = useState([]);
  const [activeMetadataEntryId, setActiveMetadataEntryId] = useState('');
  const [forceMetadataValidationVisible, setForceMetadataValidationVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const metadataEntryRefs = useRef(new Map());
  const metadataBlurTimeoutRef = useRef(null);

  const exportChecklistAnalysisCsv = useMemo(
    () => httpsCallable(firebaseFunctions, 'exportChecklistAnalysisCsv'),
    []
  );

  useEffect(() => {
    if (!open) return;

    setDatePreset('last7');
    setDateFrom('');
    setDateTo('');
    setMetadataFilters(cloneMetadataFilters(defaultMetadataFilters));
    setActiveMetadataEntryId('');
    setForceMetadataValidationVisible(false);
    setExporting(false);
    setExportError('');
  }, [open, defaultMetadataFilters]);

  useEffect(() => () => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
    }
  }, []);

  const hasIncompleteMetadataEntry = useMemo(
    () => metadataFilters.some((entry) => {
      const key = trimString(entry.key);
      const value = trimString(entry.value);
      return Boolean(key || value) && (!key || !value);
    }),
    [metadataFilters]
  );

  const metadataValidationMessage = hasIncompleteMetadataEntry
    && (forceMetadataValidationVisible || !activeMetadataEntryId)
    ? 'Ավարտեք կամ հեռացրեք կիսատ մետատվյալների դաշտերը։'
    : '';

  const preparedMetadataFilters = useMemo(() => (
    Array.from(metadataFilters.reduce((groupMap, filter) => {
      const key = trimString(filter.key);
      const value = trimString(filter.value);

      if (!key || !value) return groupMap;

      if (!groupMap.has(key)) {
        groupMap.set(key, new Set());
      }

      groupMap.get(key).add(String(value));
      return groupMap;
    }, new Map()).entries()).map(([key, selectedSet]) => ({
      key,
      values: Array.from(selectedSet),
    }))
  ), [metadataFilters]);

  const handleAddMetadataFilter = () => {
    setForceMetadataValidationVisible(false);
    setMetadataFilters((prev) => [...prev, createMetadataFilter()]);
  };

  const handleRemoveMetadataFilter = (filterId) => {
    setForceMetadataValidationVisible(false);
    setActiveMetadataEntryId((prev) => (prev === filterId ? '' : prev));
    setMetadataFilters((prev) => prev.filter((filter) => filter.id !== filterId));
  };

  const handleClearMetadataFilters = () => {
    setForceMetadataValidationVisible(false);
    setActiveMetadataEntryId('');
    setMetadataFilters([]);
  };

  const updateMetadataFilter = (filterId, updates) => {
    setForceMetadataValidationVisible(false);
    setMetadataFilters((prev) => prev.map((filter) => (
      filter.id === filterId ? { ...filter, ...updates } : filter
    )));
  };

  const handleMetadataFieldFocus = (entryId) => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
      metadataBlurTimeoutRef.current = null;
    }
    setActiveMetadataEntryId(entryId);
    setForceMetadataValidationVisible(false);
  };

  const handleMetadataFieldBlur = (entryId) => {
    if (metadataBlurTimeoutRef.current) {
      clearTimeout(metadataBlurTimeoutRef.current);
    }

    metadataBlurTimeoutRef.current = window.setTimeout(() => {
      const entryNode = metadataEntryRefs.current.get(entryId);
      const activeElement = document.activeElement;
      if (entryNode && activeElement && entryNode.contains(activeElement)) {
        return;
      }
      setActiveMetadataEntryId((prev) => (prev === entryId ? '' : prev));
    }, 0);
  };

  const handleExport = async () => {
    if (hasIncompleteMetadataEntry) {
      setForceMetadataValidationVisible(true);
      return;
    }

    if (!checklist?.id || exporting) return;

    setExporting(true);
    setExportError('');

    try {
      let fromMs = 0;
      let toMs = 0;

      if (datePreset === 'custom') {
        if (dateFrom) {
          const start = new Date(dateFrom);
          fromMs = Number.isNaN(start.getTime()) ? 0 : start.getTime();
        }

        if (dateTo) {
          const endDate = new Date(dateTo);
          const end = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
            23,
            59,
            59,
            999
          );
          toMs = Number.isNaN(end.getTime()) ? 0 : end.getTime();
        }
      } else {
        const range = getRangeFromPreset(datePreset);
        fromMs = range.from ? range.from.getTime() : 0;
        toMs = range.to ? range.to.getTime() : 0;
      }

      const response = await exportChecklistAnalysisCsv({
        appOrigin: window.location.origin,
        checklistId: checklist.id,
        dateFromMs: fromMs,
        dateToMs: toMs,
        metadataFilters: preparedMetadataFilters,
      });

      const downloadUrl = trimString(response?.data?.downloadUrl);
      if (!downloadUrl) {
        throw new Error('Missing download URL');
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.rel = 'noopener noreferrer';
      const fileName = trimString(response?.data?.fileName);
      if (fileName) {
        link.download = fileName;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onClose();
    } catch (error) {
      console.error('Error exporting checklist CSV:', error);
      setExportError(normalizeCallableErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={exporting ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ pb: 1 }}>
        CSV արտահանում
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Ընտրեք ժամանակային միջակայքը և մետատվյալների ֆիլտրերը։ CSV ֆայլում կլինեն միայն ավարտված վերլուծությունները։
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              label="Ժամանակային միջակայք"
              value={datePreset}
              onChange={(event) => setDatePreset(event.target.value)}
              fullWidth
            >
              <MenuItem value="all">Ամբողջ ժամանակահատվածը</MenuItem>
              <MenuItem value="today">Այսօր</MenuItem>
              <MenuItem value="yesterday">Երեկ</MenuItem>
              <MenuItem value="last7">Վերջին 7 օրը</MenuItem>
              <MenuItem value="thisWeek">Այս շաբաթ</MenuItem>
              <MenuItem value="lastWeek">Անցյալ շաբաթ</MenuItem>
              <MenuItem value="thisMonth">Այս ամիս</MenuItem>
              <MenuItem value="lastMonth">Անցյալ ամիս</MenuItem>
              <MenuItem value="custom">Անհատական միջակայք</MenuItem>
            </TextField>
          </Stack>

          {datePreset === 'custom' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Սկիզբ"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Ավարտ"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#fbfcff' : 'background.paper'),
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Մետատվյալների ֆիլտրեր
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Զտեք արտահանումը ձայնագրության մետատվյալների հիման վրա
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddMetadataFilter}
                    disabled={metadataKeyOptions.length === 0}
                  >
                    Ավելացնել դաշտ
                  </Button>

                  {metadataFilters.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearMetadataFilters}
                    >
                      Մաքրել ֆիլտրերը
                    </Button>
                  )}
                </Stack>
              </Stack>

              {metadataValidationMessage && (
                <Alert severity="warning" sx={{ py: 0 }}>
                  {metadataValidationMessage}
                </Alert>
              )}

              {metadataKeyOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Մետատվյալներ դեռ չկան։
                </Typography>
              ) : metadataFilters.length > 0 ? (
                <Stack spacing={1.25}>
                  {metadataFilters.map((filter) => {
                    const selectedKey = trimString(filter.key);
                    const valueOptions = selectedKey ? metadataValuesByKey[selectedKey] || [] : [];

                    return (
                      <Paper
                        key={filter.id}
                        ref={(node) => {
                          if (node) {
                            metadataEntryRefs.current.set(filter.id, node);
                          } else {
                            metadataEntryRefs.current.delete(filter.id);
                          }
                        }}
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 2.5,
                          backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#ffffff' : 'background.paper'),
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                          <Autocomplete
                            freeSolo
                            options={metadataKeyOptions}
                            value={filter.key || ''}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'reset') return;
                              updateMetadataFilter(filter.id, { key: value });
                            }}
                            onChange={(_, value) => {
                              updateMetadataFilter(filter.id, {
                                key: typeof value === 'string' ? value : value || '',
                                value: '',
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label="Մետատվ. բանալի"
                                placeholder="օր. բաժին"
                                onFocus={() => handleMetadataFieldFocus(filter.id)}
                                onBlur={() => handleMetadataFieldBlur(filter.id)}
                              />
                            )}
                            sx={{ minWidth: { xs: '100%', md: 220 }, flex: 1 }}
                          />

                          <Autocomplete
                            freeSolo
                            options={valueOptions}
                            value={filter.value || ''}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'reset') return;
                              updateMetadataFilter(filter.id, { value });
                            }}
                            onChange={(_, value) => {
                              updateMetadataFilter(filter.id, {
                                value: typeof value === 'string' ? value : value || '',
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label="Արժեք"
                                placeholder="օր. վաճառք"
                                onFocus={() => handleMetadataFieldFocus(filter.id)}
                                onBlur={() => handleMetadataFieldBlur(filter.id)}
                              />
                            )}
                            sx={{ minWidth: { xs: '100%', md: 240 }, flex: 1.1 }}
                          />

                          <Tooltip title="Հեռացնել մետատվյալը">
                            <IconButton
                              onClick={() => handleRemoveMetadataFilter(filter.id)}
                              sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : null}
            </Stack>
          </Paper>

          <Alert severity="info">
            Արտահանումը կպատրաստվի սերվերում։ Մեծ ցուցակների դեպքում դա կարող է մի փոքր տևել, և CSV-ում կլինեն միայն ավարտված վերլուծությունները։
          </Alert>

          {exportError && (
            <Alert severity="error">
              {exportError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={exporting}>
          Չեղարկել
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleExport}
          disabled={exporting || !checklist?.id || hasIncompleteMetadataEntry}
        >
          {exporting ? 'Արտահանվում է...' : 'Արտահանել CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistAnalysisExportDialog;
